import { useQuery } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Modal,
  Select,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  type OrganizationUsers,
  organizationsTeamUsersListOptions
} from 'src/api/main'
import { UserCell } from 'src/components'
import { paths } from 'src/router'
import { composeDisplayName } from 'src/utils/string'

import { useTeamMemberMutations } from '../hooks'
import type { OrgUnit } from '../types'

const { Text, Link } = Typography

type Props = {
  workspaceId: number
  unit: OrgUnit
  /** Kind color used for member avatars. */
  color: string
  /** Members of this unit (already filtered from the access list). */
  members: OrganizationUsers[]
  /** All workspace members — candidate pool for the add picker. */
  allUsers: OrganizationUsers[]
  /** Membership management is only offered for leaf teams. */
  canManage: boolean
  /** Navigate to a member's detail page. */
  onMemberClick: (userId: number) => void
}

type AddFormValues = { user: number }

export const OrgTeamMembersCard = ({
  workspaceId,
  unit,
  color,
  members,
  allUsers,
  canManage,
  onMemberClick
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal } = AntdApp.useApp()
  const [addForm] = Form.useForm<AddFormValues>()

  const [addOpen, setAddOpen] = useState(false)

  const { addMember, removeMember } = useTeamMemberMutations(workspaceId)

  // Membership records carry the team-users id the access list lacks — needed
  // to remove a member (DELETE targets the team-users row, not the access id).
  const { data: teamUsersPage } = useQuery({
    ...organizationsTeamUsersListOptions({
      query: { team: unit.id, per_page: 500 }
    }),
    enabled: canManage && Number.isFinite(unit.id)
  })

  // The detail list keys members by `organization_access_id` (the access id,
  // which is `OrganizationUsers.id`), not the user id — map it to the
  // team-users membership id.
  const membershipIdByAccessId = useMemo(() => {
    const map = new Map<number, number>()
    for (const tu of teamUsersPage?.results ?? []) {
      if (tu.organization_access_id != null) {
        map.set(tu.organization_access_id, tu.id)
      }
    }
    return map
  }, [teamUsersPage])

  const candidateOptions = useMemo(() => {
    const memberUserIds = new Set(members.map((m) => m.user.id))

    return allUsers
      .filter((u) => !memberUserIds.has(u.user.id))
      .map((u) => ({
        value: u.user.id,
        label: composeDisplayName(
          u.user.first_name,
          u.user.last_name,
          u.user.email
        )
      }))
  }, [allUsers, members])

  const submitAdd = (values: AddFormValues) => {
    addMember.mutate(
      {
        body: { user: values.user, team: unit.id }
      },
      { onSuccess: () => setAddOpen(false) }
    )
  }

  const confirmRemove = (member: OrganizationUsers) => {
    const membershipId = membershipIdByAccessId.get(member.id)
    if (membershipId == null) return
    const name = composeDisplayName(
      member.user.first_name,
      member.user.last_name,
      member.user.email
    )
    modal.confirm({
      title: t('organization.detail.members.removeConfirm.title'),
      content: t('organization.detail.members.removeConfirm.content', {
        name,
        team: unit.name
      }),
      okText: t('organization.detail.members.removeConfirm.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('organization.detail.members.removeConfirm.cancel'),
      onOk: () => removeMember.mutateAsync({ path: { id: membershipId } })
    })
  }

  return (
    <>
      <Card
        size="small"
        title={
          <Flex align="center" gap={6}>
            <Text strong style={{ fontSize: 13 }}>
              {t('organization.detail.members.title')}
            </Text>
            <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>
              {members.length}
            </Tag>
          </Flex>
        }
        extra={
          canManage ? (
            <Button
              size="small"
              type="text"
              icon={<Plus size={14} />}
              onClick={() => setAddOpen(true)}
            >
              {t('organization.detail.members.add')}
            </Button>
          ) : undefined
        }
      >
        {members.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ margin: '8px 0' }}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('organization.detail.members.empty')}
              </Text>
            }
          />
        ) : (
          <Flex vertical gap={2}>
            {members.slice(0, 8).map((m) => (
              <Flex
                key={m.id}
                align="center"
                justify="space-between"
                gap={10}
                style={{
                  padding: '7px 6px',
                  borderBottom: '1px solid var(--ant-color-border-secondary)'
                }}
              >
                <UserCell
                  firstName={m.user.first_name}
                  lastName={m.user.last_name}
                  email={m.user.email}
                  photoUrl={m.user.photo?.file ?? null}
                  color={color}
                  size="md"
                  onClick={() => onMemberClick(m.user.id)}
                />
                {canManage ? (
                  <Tooltip title={t('organization.detail.members.remove')}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={13} />}
                      disabled={!membershipIdByAccessId.has(m.id)}
                      onClick={() => confirmRemove(m)}
                      aria-label={t('organization.detail.members.remove')}
                    />
                  </Tooltip>
                ) : null}
              </Flex>
            ))}
            {members.length > 8 ? (
              <Link
                style={{ fontSize: 12, marginTop: 4 }}
                onClick={() =>
                  navigate(
                    `${paths.workspaces.members(String(workspaceId))}?team=${unit.id}`
                  )
                }
              >
                {t('organization.detail.members.showAll', {
                  count: members.length
                })}
              </Link>
            ) : null}
          </Flex>
        )}
      </Card>

      <Modal
        open={addOpen}
        title={t('organization.detail.members.addModal.title', {
          team: unit.name
        })}
        okText={t('organization.detail.members.addModal.submit')}
        cancelText={t('organization.detail.members.addModal.cancel')}
        confirmLoading={addMember.isPending}
        onOk={() => addForm.submit()}
        onCancel={() => setAddOpen(false)}
        destroyOnHidden
      >
        <Form<AddFormValues>
          form={addForm}
          layout="vertical"
          onFinish={submitAdd}
          preserve={false}
        >
          <Form.Item
            name="user"
            label={t('organization.detail.members.addModal.userLabel')}
            rules={[
              {
                required: true,
                message: t('organization.detail.members.addModal.userRequired')
              }
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t(
                'organization.detail.members.addModal.userPlaceholder'
              )}
              options={candidateOptions}
              notFoundContent={t(
                'organization.detail.members.addModal.noCandidates'
              )}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
